/**
 * Pure flush-packing of a free wall sub-span into a bay sequence.
 *
 * Extracted from `fillRun.js` so both the manual cabinet-run engine AND the
 * auto kitchen path (`kitchen-assembly/materializeCandidate`) can share ONE
 * module-splitting algorithm — instead of the auto path emitting one coarse
 * box per span. See claude-docs/2026-06-30-cabinet-abilities-into-auto-kitchen.md.
 *
 * Pure. No geometry, no wall objects — just 1D param math.
 */

const {
  solveModuleSequence,
  solveEqualModuleSequence,
  symmetricOrder,
  MIN_FILLER_IN,
} = require('./moduleSolver');
const {
  preferredFillerEnd,
  preferredSlackEnd,
  fillerEndRank,
} = require('./fillerEnd');

const SPAN_EPS = 1e-4;
/**
 * A column standing closer to a span end than a filler can be IS at that end —
 * `fillZoneBounds`'s own test for "this zone ends at the wall", borrowed for
 * the same reason: a gap narrower than ¼" is not a gap anything can fill.
 */
const SEAT_FLUSH = MIN_FILLER_IN * 0.0254;

/**
 * Push one fixed entry at `cursor` and return the cursor after it.
 *
 * An entry flagged `splitIntoModules` (a tall span — `bayOverrides.js` sets
 * the flag, this file never reads a type) is packed as the fewest, most equal
 * modules of its own kind (`solveEqualModuleSequence`, under the same family
 * `opts` the free span gets) plus one trailing filler, with every piece
 * answering to the one override, so selecting any box of the span edits the
 * span. A sub-filler remainder (under `MIN_FILLER_IN`) is absorbed by
 * advancing to the block's end, exactly as a free span leaves it.
 */
function pushFixed(pieces, f, cursor, opts) {
  const end = cursor + f.width;
  if (!f.splitIntoModules) {
    pieces.push({ kind: 'fixed', start: cursor, end, override: f.override });
    return end;
  }
  // A vendor family packs a tall span from its own pantry widths
  // (`familyFillOpts`'s `tallWidthsIn`); every other family as before.
  const tallOpts =
    opts && opts.tallWidthsIn
      ? { ...opts, allowedWidthsIn: opts.tallWidthsIn }
      : opts;
  const { modules, filler } = solveEqualModuleSequence(f.width, tallOpts);
  let c = cursor;
  for (const m of modules) {
    pieces.push({
      kind: 'fixed',
      start: c,
      end: c + m.widthMeters,
      override: f.override,
    });
    c += m.widthMeters;
  }
  if (filler) {
    pieces.push({
      kind: 'filler',
      start: c,
      end: c + filler.widthMeters,
      override: f.override,
    });
  }
  return end;
}

/**
 * The order equal modules stand in. Symmetric where the widths allow
 * (`symmetricOrder`). Where they do not — 24 · 21 — the WIDER box goes toward
 * the more prominent end, which is the one a scribe would least want to stand
 * at (`fillerEndRank`): beside a hood or a window rather than dying into a
 * wall. So the two flanks of a hood come out as mirror images,
 * 21 · 24 | hood | 24 · 21, instead of both reading left to right. A tie keeps
 * the solver's largest-first.
 */
function orderEqualModules(widths, loBound, hiBound) {
  const ordered = symmetricOrder(widths);
  const symmetric = ordered.every(
    (w, i) => Math.abs(w - ordered[ordered.length - 1 - i]) < 1e-9
  );
  if (symmetric) return ordered;
  return fillerEndRank(loBound) > fillerEndRank(hiBound)
    ? [...ordered].reverse()
    : ordered;
}

/**
 * Pack one FREE sub-span [lo, hi] — no hard pieces inside — as a single
 * flush sequence.
 *
 * The auto space (the span minus the fixed-bay widths) is solved as ONE module
 * run + one filler, then everything packs edge-to-edge: auto cabinets and the
 * fixed-width bays (in anchor order) slide together with no gaps, and the lone
 * filler lands at ONE END. A fixed bay therefore "slides" — its anchor only
 * sets its order and roughly where among the auto cabinets it lands (the auto
 * boundary nearest the anchor), never a hard position. This keeps a width edit
 * from stranding slivers around the edited bay: any remainder collects as one
 * section at an end of the sub-span.
 *
 * WHICH end is `preferredFillerEnd`'s call, from `opts.loBound` /
 * `opts.hiBound` — descriptors for what stands at each end of this span. A
 * filler is a scribe, so it goes where it can die into something (a wall, a
 * corner, a tall unit) rather than beside the sink or mid-field. With no
 * descriptors it trails, exactly as it always did. A leftover under a
 * filler's width is no piece at all, and is left at the end where a bare slot
 * shows least (`preferredSlackEnd`) — else trailing, as before.
 *
 * `fixed` is a (possibly empty) list of
 * `{ anchorParam, width, override, splitIntoModules? }` — the auto kitchen path
 * passes `[]` (no per-bay width overrides); the manual editor passes the user's
 * fixed-width bays.
 *
 * Returns { pieces: [{ kind:'auto'|'fixed'|'filler', start, end, override? }],
 * overflow } where overflow=true when Σ fixed widths > span; a filler piece
 * carries `override` only when it is a span's remainder.
 */
function packFreeSpan(lo, hi, fixed, opts = {}) {
  const pieces = [];
  const span = hi - lo;
  if (span <= SPAN_EPS) return { pieces, overflow: false };

  const sorted = [...fixed].sort((a, b) => a.anchorParam - b.anchorParam);
  const fixedTotal = sorted.reduce((s, f) => s + f.width, 0);

  // Over-constrained: the fixed widths alone exceed the span. Pack them flush
  // and flag it; no auto cabinets or filler fit.
  if (fixedTotal > span + SPAN_EPS) {
    let cursor = lo;
    for (const f of sorted) cursor = pushFixed(pieces, f, cursor, opts);
    // WHAT did not fit, so the message can name it. "Fixed cabinet widths
    // exceed the available space" is true and unactionable: the reader cannot
    // tell which cabinet, or by how much, and the offending bay is packed
    // flush somewhere else entirely, so the drawing does not show them either.
    return {
      pieces,
      overflow: true,
      overflowDetail: {
        lo,
        hi,
        span,
        widths: sorted.map((f) => f.width),
        overrideIds: sorted.map((f) => f.override?.id).filter(Boolean),
      },
    };
  }

  // `opts.equalModules`: divide the auto space EQUALLY rather than greedily —
  // an upper tier packed on its own (`upperSpec.align: 'independent'`), where
  // 30 · 30 · 30 · 30 reads as intended and 36 · 36 · 36 · 12 does not.
  const { modules, filler } = opts.equalModules
    ? solveEqualModuleSequence(span - fixedTotal, opts)
    : solveModuleSequence(span - fixedTotal, opts);
  const autos = opts.equalModules
    ? orderEqualModules(
        modules.map((m) => m.widthMeters),
        opts.loBound,
        opts.hiBound
      )
    : modules.map((m) => m.widthMeters);
  const fillerW = filler ? filler.widthMeters : 0;
  // A leading filler is emitted BEFORE anything else, so every module and
  // fixed bay after it starts that much further along. Nothing else changes:
  // the sequence still tiles [lo, hi] exactly, and a fixed bay still lands at
  // the auto boundary nearest its anchor — just measured from the new origin.
  const atLo =
    filler && preferredFillerEnd(opts.loBound, opts.hiBound) === 'lo';
  if (atLo) pieces.push({ kind: 'filler', start: lo, end: lo + fillerW });
  // A leftover too narrow for a filler is left bare — at the end where a slot
  // shows least (`preferredSlackEnd`): the run's own end, a corner reveal that
  // takes it in, else beside an appliance or a column. The pieces start that
  // much further along when it is the LOW end; at the high end they simply
  // stop short, as they always did.
  const autoTotal = autos.reduce((s, w) => s + w, 0);
  const slack = filler ? 0 : Math.max(0, span - fixedTotal - autoTotal);
  const slackAtLo =
    slack > SPAN_EPS && preferredSlackEnd(opts.loBound, opts.hiBound) === 'lo';

  let cursor = atLo ? lo + fillerW : slackAtLo ? lo + slack : lo;
  let ai = 0;
  // Emit auto cabinets whose centre falls at/before `limit` (so a fixed bay
  // lands at the auto boundary nearest its anchor).
  const emitAutosUntil = (limit) => {
    while (ai < autos.length && cursor + autos[ai] / 2 <= limit + SPAN_EPS) {
      pieces.push({ kind: 'auto', start: cursor, end: cursor + autos[ai] });
      cursor += autos[ai];
      ai += 1;
    }
  };

  for (const f of sorted) {
    emitAutosUntil(f.anchorParam - f.width / 2);
    cursor = pushFixed(pieces, f, cursor, opts);
  }
  emitAutosUntil(Infinity); // any auto cabinets after the last fixed bay
  if (filler && !atLo) {
    pieces.push({ kind: 'filler', start: cursor, end: cursor + fillerW });
  }
  return { pieces, overflow: false };
}

/**
 * Plan a sub-span [lo, hi] that may contain HARD-CENTRED pieces.
 *
 * A hard piece is a bay whose centre is decided by geometry, not by packing:
 * an appliance the scan measured or the user dropped, an opening that
 * blocks the counter, a bay a `center-on` / `frame` link pins to a window.
 * It partitions the span exactly as an obstacle used to — the free spans
 * between hard pieces are packed by `packFreeSpan` as before — but it comes
 * back as a piece with identity (`kind:'hard'`, `bay` echoed), so the run
 * can move the objects that live in it. A hard piece NEVER slides; one that
 * stands off the end of the span is clamped in and reported in
 * `unsatisfied`, never silently moved.
 *
 * `hard` items: `{ center, width, bay, pad?, seats? }` — `bay` is opaque and
 * echoed back. `pad` (metres, default 0) is a strip either side of the piece
 * that the free spans beside it must not enter, without being part of the
 * piece: a fridge's surround panels stand there. The piece comes back at its
 * own width; the padded strips come back as nothing. `seats` is the ONE
 * exception to "never slides" — see `seatColumnAtEnd` — `avoid`
 * (`[{ lo, hi }]`) is what a seating piece may not slide across, and
 * `wallFiller` the least it keeps between itself and a WALL.
 * Overlapping hard pieces are kept where they are (their carved region is
 * the union) and reported `overlap`; only the span ends clamp.
 * `fixed` overrides are assigned to whichever free sub-span contains their
 * `anchorParam`.
 *
 * `opts.loBound` / `opts.hiBound` describe what stands at the two OUTER ends
 * of [lo, hi] (see `fillerEnd.js` for the vocabulary); the inner ends of each
 * free sub-span are the hard pieces' own `bay.kind`. Together they decide
 * which end each sub-span's filler goes to.
 *
 * Returns `{ pieces, overflow, unsatisfied }`:
 *   pieces      [{ kind:'auto'|'fixed'|'filler'|'hard', start, end, override?, bay? }]
 *   overflow    true when some free sub-span's fixed widths exceed it
 *   unsatisfied [{ bay, reason:'clamped'|'overlap', delta }]
 */
/**
 * The adjacent free stretch's REMAINDER: what `packFreeSpan` would leave as a
 * filler there. A user's own filler bay carries its override and is not one.
 */
function remainderOf(a, b, fixed, opts) {
  if (b - a <= SPAN_EPS) return 0;
  return packFreeSpan(a, b, fixed, opts)
    .pieces.filter((p) => p.kind === 'filler' && !p.override)
    .reduce((sum, p) => sum + (p.end - p.start), 0);
}

/**
 * SEAT a column that stands at an end of the span — the one hard piece that
 * moves, and only ever by this rule.
 *
 * A fridge is a box the run wraps in an install WIDER than the box: ½" side
 * panels either side (`pad`). "A hard piece never slides" pinned the BOX flush
 * at the wall end the generator seats it to (`seatFridgeAtOpenRunEnd`), which
 * did two things, measured over 111 generated layouts on 2026-09-20:
 *
 *  - 52 surrounds stood with a panel half an inch INSIDE the side wall. The
 *    earlier fix (`pad`) kept packed cabinets off the panel; nothing kept the
 *    panel out of the wall.
 *  - the stretch's remainder landed BETWEEN the fridge and the cabinets, on
 *    every one of them that had one. `fillerEnd.js` ranks a wall (5) well
 *    above beside-the-fridge (3) — but the wall was never an end of the free
 *    span, because the fridge was pinned against it. A strip there is also the
 *    room a fridge door needs to open past a side wall.
 *
 * So a seating piece within a filler's width of an end (a) stands with its
 * PADDED edge on that end, and (b) where the end outranks its own kind, slides
 * inboard by the adjacent stretch's remainder, which comes back as a filler
 * between the end and the column. The stretch then packs with nothing left
 * over. Where the end does not outrank it — an open run end, a doorway — the
 * remainder stays beside the fridge, as the ranking says.
 *
 * Not reported as `unsatisfied`: this is where the piece belongs, not a piece
 * that could not stand where it was. The caller moves the appliance to the
 * solved bay, exactly as it does for any clamp.
 *
 * Mutates `h` (its `start` / `end`). Returns the strip the column gave up —
 * `{ start, end, filler }`, `filler: false` for one left empty — or null.
 */
function seatColumnAtEnd(h, next, prev, lo, hi, fixedIn, opts) {
  // AT an end: flush with it — or with nothing between the two but what could
  // only ever be a filler. That strip is the column's, whoever left it there:
  //  - at a WALL it is the fridge's wall filler (the generator stands a
  //    wall-end fridge off the wall for exactly this; an older kitchen stands
  //    flush), and the seat RE-DERIVES it from the wall. Re-seating from the
  //    end each time is what makes the result the same from either start, and
  //    idempotent;
  //  - at an OPEN run end there is nothing for it to close against, so it is
  //    left empty — and the column is NOT brought to the end. The generator
  //    stands a fridge in from an open end for a reason the packer cannot see
  //    (its 30" depth clearing a wing wall a 24" cabinet passes), and the
  //    filler that stretch used to pack — built at the COLUMN's depth, as a
  //    scribe to a fridge is — stood 2.2" through that very wall.
  const onlyFiller = (x0, x1) =>
    x1 - x0 > SPAN_EPS &&
    Math.abs(remainderOf(x0, x1, fixedIn(x0, x1), opts) - (x1 - x0)) < SPAN_EPS;
  const ownsStrip = (bound) => bound === 'wall' || bound === 'open-end';
  const flushLo = h.start - h.pad <= lo + SEAT_FLUSH;
  const flushHi = h.end + h.pad >= hi - SEAT_FLUSH;
  const atLo =
    flushLo || (ownsStrip(opts.loBound) && onlyFiller(lo, h.start - h.pad));
  const atHi =
    flushHi || (ownsStrip(opts.hiBound) && onlyFiller(h.end + h.pad, hi));
  // Spanning the whole zone, or standing at neither end: nothing to seat.
  if (atLo === atHi) return null;
  const endBound = atLo ? opts.loBound : opts.hiBound;
  // WEDGED: a hard neighbour stands so close on the inboard side that the
  // column cannot come off the end without going into it. Hard pieces are
  // never pushed apart here, so it stays exactly where it was — seating it
  // anyway turned a ½" overlap with a dishwasher into a full inch.
  const neighbour = atLo ? next : prev;
  if (neighbour) {
    const room = atLo
      ? neighbour.start - neighbour.pad - lo
      : hi - (neighbour.end + neighbour.pad);
    if (room < h.width + 2 * h.pad - SPAN_EPS) return null;
  }
  // Where the column stands before it comes in: ON the end — except at an
  // open end it was stood in from, where it is never moved toward the end.
  const onEnd = atLo ? lo + h.pad : hi - h.pad - h.width;
  const keepsOff = endBound === 'open-end' && !(atLo ? flushLo : flushHi);
  const seated = keepsOff ? h.start : onEnd;
  // The free stretch on the column's INBOARD side, up to the next hard piece.
  const a = atLo ? seated + h.width + h.pad : prev ? prev.end + prev.pad : lo;
  const b = atLo ? (next ? next.start - next.pad : hi) : seated - h.pad;
  // What the end is decides what becomes of the stretch's remainder:
  //  - it OUTRANKS the piece (a wall, a corner): the strip is a filler between
  //    the end and the column;
  //  - it is an OPEN run end: there is nothing beyond the fridge for a strip
  //    to clear, so the column simply comes in by that much and the strip is
  //    left EMPTY — the run ends that much sooner, with no filler anywhere
  //    (user ruling 2026-09-20; it used to stay beside the fridge);
  //  - anything else: the column seats and the remainder stays where the
  //    ranking puts it.
  const yields = fillerEndRank(endBound) > fillerEndRank(h.bay?.kind);
  const recedes = endBound === 'open-end';
  const stretchBounds = atLo
    ? {
        ...opts,
        loBound: h.bay?.kind,
        hiBound: next ? next.bay?.kind : opts.hiBound,
      }
    : {
        ...opts,
        loBound: prev ? prev.bay?.kind : opts.loBound,
        hiBound: h.bay?.kind,
      };
  // The remainder the stretch is left with once the column has come in by
  // `by` — the stretch is that much shorter at the column's end of it.
  const remainderAfter = (by) =>
    atLo
      ? remainderOf(a + by, b, fixedIn(a + by, b), stretchBounds)
      : remainderOf(a, b - by, fixedIn(a, b - by), stretchBounds);
  const remainder = yields || recedes ? remainderAfter(0) : 0;
  // Against a WALL the strip has a job beyond being a scribe: it is the room
  // the fridge door needs to swing past the wall, so it is never narrower
  // than `wallFiller` (the caller's — 3" for a fridge). The stretch gives up
  // that much and whatever it then cannot pack rides along, so the cabinets
  // still tile exactly. A stretch too short to give it up keeps the remainder.
  //
  // …and never narrower than it ALREADY is. The strip is re-derived from the
  // wall, but a reveal someone left on purpose — a fridge dropped 7" off the
  // wall — is theirs: it may grow by what the stretch cannot pack, it is not
  // cut back to the floor. (The first cut did, and turned a 7" reveal into 4".)
  const gap = Math.max(0, atLo ? h.start - h.pad - lo : hi - (h.end + h.pad));
  // A WALL's alone. Everywhere else the slide is the bare remainder: at an
  // open end the column was never brought to the end, so its gap is already
  // kept (`seated` above) — counting it again here slid a fridge that should
  // have come in 0.7" by 6.7", and took 6" off the cabinet beside it (caught in
  // the user's own kitchen, 2026-09-20, an hour after it was written).
  const floor = endBound === 'wall' ? Math.max(gap, h.wallFiller || 0) : 0;
  const widened =
    floor > remainder + SPAN_EPS && b - a >= floor - SPAN_EPS
      ? floor + remainderAfter(floor)
      : remainder;
  // Coming off the wall must not cost a WINDOW. The lower fill does not see
  // one a base cabinet passes under, so the free stretch beside a fridge can
  // run straight across glass the fridge itself may not stand in front of
  // (`avoid` — the caller's list for THIS piece). Seating clear of the wall
  // is not negotiable, a panel cannot stand inside one; sliding further is,
  // so a slide that would put more of the column over an `avoid` range than
  // the seat alone does is not taken, and the remainder stays where the
  // ranking puts it.
  const over = (start) =>
    (h.avoid || []).reduce(
      (sum, r) =>
        sum +
        Math.max(
          0,
          Math.min(r.hi, start + h.width + h.pad) -
            Math.max(r.lo, start - h.pad)
        ),
      0
    );
  const at = (by) => (atLo ? seated + by : seated - by);
  // The widest slide that costs no glass: the full strip, else the bare
  // remainder, else none.
  const slide =
    [widened, remainder].find(
      (by) => over(at(by)) <= over(seated) + SPAN_EPS
    ) ?? 0;
  h.start = at(slide);
  h.end = h.start + h.width;
  // Everything between the end and the column's panel is the strip.
  const strip = atLo
    ? { start: lo, end: h.start - h.pad }
    : { start: h.end + h.pad, end: hi };
  if (!(strip.end - strip.start > SPAN_EPS)) return null;
  // An open end's strip is left empty: nothing stands in it.
  return { ...strip, filler: !recedes };
}

const fillerPiece = (strip) => ({
  kind: 'filler',
  start: strip.start,
  end: strip.end,
});

function planSubSpanSequence(lo, hi, fixed = [], hard = [], opts = {}) {
  const unsatisfied = [];
  if (!hard || hard.length === 0) {
    const r = packFreeSpan(lo, hi, fixed || [], opts);
    return {
      pieces: r.pieces,
      overflow: r.overflow,
      overflowDetails: r.overflowDetail ? [r.overflowDetail] : [],
      unsatisfied,
    };
  }

  const placed = hard
    .map((h) => ({
      bay: h.bay,
      width: h.width,
      pad: h.pad || 0,
      seats: h.seats === true,
      avoid: h.avoid,
      wallFiller: h.wallFiller,
      start: h.center - h.width / 2,
      end: h.center + h.width / 2,
    }))
    .sort((a, b) => a.start - b.start);
  const inSpan = (a, b) =>
    (fixed || []).filter(
      (f) => f.anchorParam >= a - SPAN_EPS && f.anchorParam <= b + SPAN_EPS
    );
  // Columns seat FIRST, so the clamp below sees them where they belong and
  // reports only a piece that genuinely cannot stand.
  const endFillers = new Map();
  placed.forEach((h, i) => {
    if (!h.seats) return;
    const filler = seatColumnAtEnd(
      h,
      placed[i + 1],
      placed[i - 1],
      lo,
      hi,
      inSpan,
      opts
    );
    if (filler) endFillers.set(h, filler);
  });

  // Clamp into [lo, hi]. Two hard pieces that overlap (two scanned
  // appliances a few mm into each other, a pantry reservation over a fridge)
  // are BOTH left where they stand and reported — the carved region is their
  // union, exactly as the old obstacle loop treated it. Pushing the second
  // one clear would move a measured appliance to resolve scan noise, and on
  // real kitchens it swallowed a wall of cabinets.
  for (const h of placed) {
    let start = h.start;
    if (start < lo - SPAN_EPS) {
      unsatisfied.push({ bay: h.bay, reason: 'clamped', delta: lo - start });
      start = lo;
    }
    if (start + h.width > hi + SPAN_EPS) {
      const clampedStart = Math.max(lo, hi - h.width);
      unsatisfied.push({
        bay: h.bay,
        reason: 'clamped',
        delta: start - clampedStart,
      });
      start = clampedStart;
    }
    h.start = start;
    h.end = start + h.width;
  }
  for (let i = 1; i < placed.length; i++) {
    const prev = placed[i - 1];
    const cur = placed[i];
    if (cur.start < prev.end - SPAN_EPS) {
      unsatisfied.push({
        bay: cur.bay,
        reason: 'overlap',
        delta: prev.end - cur.start,
      });
    }
  }

  const pieces = [];
  let overflow = false;
  const overflowDetails = [];
  // Each free sub-span is bounded by the hard piece either side of it, or —
  // at the two outer ends — by whatever the caller says bounds the whole zone.
  // That pair is what decides which end this sub-span's filler goes to.
  const withBounds = (loBound, hiBound) => ({ ...opts, loBound, hiBound });
  let free = lo;
  let prevBound = opts.loBound;
  for (const h of placed) {
    // an overlapping piece starts inside the previous one: no free span
    const keepStart = h.start - h.pad;
    // A column seated at the LOW end: the strip it yielded to the wall is its
    // filler, already sized — not a free span to pack again.
    const lead = endFillers.get(h);
    if (lead && lead.start <= lo + SPAN_EPS) {
      if (lead.filler) pieces.push(fillerPiece(lead));
      free = keepStart;
    }
    if (keepStart - free > SPAN_EPS) {
      const r = packFreeSpan(
        free,
        keepStart,
        inSpan(free, keepStart),
        withBounds(prevBound, h.bay?.kind)
      );
      pieces.push(...r.pieces);
      overflow = overflow || r.overflow;
      if (r.overflowDetail) overflowDetails.push(r.overflowDetail);
    }
    pieces.push({ kind: 'hard', start: h.start, end: h.end, bay: h.bay });
    free = Math.max(free, h.end + h.pad);
    prevBound = h.bay?.kind;
    // …and at the HIGH end, where the filler follows the column.
    if (lead && lead.end >= hi - SPAN_EPS) {
      if (lead.filler) pieces.push(fillerPiece(lead));
      free = hi;
    }
  }
  if (hi - free > SPAN_EPS) {
    const r = packFreeSpan(
      free,
      hi,
      inSpan(free, hi),
      withBounds(prevBound, opts.hiBound)
    );
    pieces.push(...r.pieces);
    overflow = overflow || r.overflow;
    if (r.overflowDetail) overflowDetails.push(r.overflowDetail);
  }
  return { pieces, overflow, overflowDetails, unsatisfied };
}

/**
 * Indices of the pieces that would stand STRANDED: every maximal group of
 * non-hard pieces that packed to nothing but filler, bounded on BOTH sides by
 * an opening keep-out or an open span end.
 *
 * A filler is a scribe — it closes the gap between two solid things. A
 * `clearance` keep-out (a walkway the layout holds open) is no more solid
 * than a doorway, so a filler-only group beside one is stranded too. Below
 * the narrowest module (the default grid's 9", or the product family's own
 * minimum — `solveModuleSequence` already yields filler-only for either) the
 * packer emits one anyway, so a run that ends a couple of inches past a
 * doorway's keep-out gets a lone 2" panel on the far side of the door, with a
 * matching sliver upper and a sliver countertop over it. Nothing on either
 * side of that strip is cabinetry, so nothing is being scribed to. A
 * filler-only group beside an appliance bay, a corner unit, a tall unit, or a
 * bound something else fills (a corner cabinet's reservation, a dishwasher's
 * end-piece strip) is kept exactly as before.
 *
 * One pairing is stranded although one side is solid: a CORNER and a keep-out.
 * Between a corner unit (or the bound a corner trims the span to) and a
 * doorway's keep-out the run butts into the corner, and the sliver past it is
 * left empty — a 1.1" filler beside an angled corner's kite and a 3" reveal
 * beside a square corner unit, each under a counter stub, was what the
 * scribe rule built there (the user, 2026-11-03: "the run should just butt
 * into the corner at that location"). A corner with the run's open end
 * beyond it, or with cabinetry beyond it, still keeps its filler.
 *
 * `openLo` / `openHi`: true when that span end is the run's own drawn extent
 * (nothing beyond it), false when it is a trimmed bound that another pass
 * builds against. Both default to true. `cornerLo` / `cornerHi`: true when
 * that span end is trimmed to a corner. Both default to false.
 */
function strandedFillerIndices(
  pieces,
  { openLo = true, openHi = true, cornerLo = false, cornerHi = false } = {}
) {
  const stranded = new Set();
  const keepOut = (p) =>
    p?.kind === 'hard' &&
    (p.bay?.kind === 'opening' || p.bay?.kind === 'clearance');
  const nothingAt = (p, open) => (p ? keepOut(p) : open);
  const cornerAt = (p, atCorner) =>
    p ? p.kind === 'hard' && p.bay?.kind === 'corner' : atCorner;
  let i = 0;
  while (i < pieces.length) {
    if (pieces[i].kind === 'hard') {
      i += 1;
      continue;
    }
    let j = i;
    while (j < pieces.length && pieces[j].kind !== 'hard') j += 1;
    const fillerOnly = pieces.slice(i, j).every((p) => p.kind === 'filler');
    const lo = pieces[i - 1];
    const hi = pieces[j];
    if (
      fillerOnly &&
      ((nothingAt(lo, openLo) && nothingAt(hi, openHi)) ||
        (cornerAt(lo, cornerLo) && keepOut(hi)) ||
        (keepOut(lo) && cornerAt(hi, cornerHi)))
    ) {
      for (let k = i; k < j; k += 1) stranded.add(k);
    }
    i = j;
  }
  return stranded;
}

module.exports = {
  planSubSpanSequence,
  packFreeSpan,
  strandedFillerIndices,
  SPAN_EPS,
};
